import React, {Fragment, MouseEvent} from 'react';

import clsx from 'clsx';
import {
    Backdrop,
    Box,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Grid,
    IconButton,
    Modal,
    ModalProps,
    Typography,
    withStyles,
} from '@material-ui/core';

import {useSpinner} from 'hooks';
import PropTypes from 'prop-types';
import {InferPropTypes} from 'commons/types/Types';
import {getModalStyle} from 'commons/utils/Functions';
import {ModalStyles} from 'assets/tss/layouts/components';
import {EfeBaseButton, EfeButtonPrimary} from 'components';
import {ReactComponent as CloseCircle} from 'assets/icons/section/action/close-circle.svg';

function EfeModal(
    props: InferPropTypes<typeof EfeModal.propTypes & ModalProps, typeof ModalStyles>
) {
    const {
        classes,
        open,
        icon,
        info,
        title,
        isLoading,
        actions,
        sizeOverride,
        reactElement,
        handleAction,
        headerActions,
        handleHeaderAction,
        ...other
    } = props;

    const [modalStyle] = React.useState(getModalStyle(sizeOverride));

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if (handleAction) {
            handleAction(event.currentTarget.id);
        }
    };

    const handleHeaderClick = (event: MouseEvent<HTMLButtonElement>, id: string) => {
        event.preventDefault();
        if (handleHeaderAction) {
            handleHeaderAction(id);
        }
    };

    const handleOnClose = (event: React.SyntheticEvent, reason: string) => {
        event.preventDefault();
        if (handleAction && reason !== 'backdropClick') {
            handleAction(reason);
        }
    };

    const headerButtons = (
        <Fragment>
            {headerActions &&
                headerActions.map((action, key) => {
                    if (action) {
                        return (
                            <IconButton
                                id={`action-${action.id}`}
                                key={`action-${key}`}
                                onClick={(event: MouseEvent<HTMLButtonElement>) =>
                                    handleHeaderClick(event, action.id)
                                }>
                                {action.icon}
                            </IconButton>
                        );
                    } else {
                        return null;
                    }
                })}
            <IconButton id='close' onClick={handleClick}>
                <CloseCircle />
            </IconButton>
        </Fragment>
    );

    const actionButtons = actions.map((action, key) => {
        switch (action) {
            case 'accept': {
                return (
                    <EfeBaseButton
                        id='accept'
                        key={key}
                        value={action}
                        variant={'outlined'}
                        onClick={handleClick}>
                        {'Confirmar'}
                    </EfeBaseButton>
                );
            }
            case 'cancel': {
                return (
                    <EfeButtonPrimary id='cancel' value={action} key={key} onClick={handleClick}>
                        {'Cancelar'}
                    </EfeButtonPrimary>
                );
            }
            case 'ok': {
                return (
                    <EfeBaseButton
                        id='ok'
                        key={key}
                        value={action}
                        variant={'outlined'}
                        onClick={handleClick}>
                        {'Aceptar'}
                    </EfeBaseButton>
                );
            }
            default: {
                return null;
            }
        }
    });
    return (
        <Modal
            className={classes.root}
            open={open}
            onClose={handleOnClose}
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
            {...other}>
            <Card style={modalStyle} className={classes.body} variant='outlined'>
                {useSpinner(isLoading)}
                <CardHeader
                    avatar={icon}
                    action={headerButtons}
                    title={title}
                    titleTypographyProps={{
                        color: 'secondary',
                        variant: 'body1',
                        fontWeight: 'bold',
                    }}
                />
                <CardContent
                    className={clsx(
                        classes.mainBody,
                        actionButtons && actionButtons[0] !== null
                            ? classes.bodyActions
                            : classes.bodyFull
                    )}>
                    <Grid
                        container
                        spacing={2}
                        alignContent='center'
                        justifyContent='center'
                        direction='row'>
                        <Grid item xs={10} sm={10} md={10}>
                            <Typography component={'span'} variant={'body2'}>
                                {reactElement}
                            </Typography>
                            <Box mt={4}>
                                <Typography
                                    component={'span'}
                                    variant={'body2'}
                                    className={classes.info}>
                                    {info}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
                {actions && <CardActions className={classes.actions}>{actionButtons}</CardActions>}
            </Card>
        </Modal>
    );
}

EfeModal.propTypes = {
    title: PropTypes.string.isRequired,
    reactElement: PropTypes.node.isRequired,
    headerActions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            icon: PropTypes.node.isRequired,
        })
    ),
    handleAction: PropTypes.func,
    handleHeaderAction: PropTypes.func,
    icon: PropTypes.node,
    sizeOverride: PropTypes.number.isRequired,
    info: PropTypes.string,
    actions: PropTypes.arrayOf(
        PropTypes.oneOf(['accept', 'decline', 'success', 'close', 'cancel', 'ok', '']).isRequired
    ).isRequired,
    isLoading: PropTypes.bool.isRequired,
    open: PropTypes.bool.isRequired,
};

EfeModal.defaultProps = {
    actions: [''],
    isLoading: false,
    sizeOverride: 50,
};

export default withStyles(ModalStyles)(EfeModal);
